import { NewStructureDto, Table, TableColumn } from '@/endpoints/models'
import { NamingDtoTypeEnum, SqlTypeEnum } from '@/types'
import { formatCodeByCase } from '@/utils'
import { assignCollectionOrder } from '@/utils/collectionOrder'
import { logError } from '@/utils/errorHandling'

import { getDefaultObjectSettings } from '../../objectSettings'
import {
	defaultHistoryTableData,
	defaultReferenceTableData,
	inflateColumns,
} from '../helpers'
import { GetTableData } from '../types'
import { callNamingWorkingData } from './callNaming'
import { stableColumnId } from './stableColumnId'

export const getTableData = async ({
	dispatch,
	node,
	table,
	generateElementCode,
	isUpperCase,
	skipCodeGeneration,
}: GetTableData) => {
	const shouldGenerateCode = generateElementCode && !skipCodeGeneration
	const nonEmptyColumns = table.form.columns
		.filter((x) => x.name)
		.map((col) => inflateColumns(col))

	const hasHistoryTable = table?.form.hasHistoryTable
	const usedColumnIds = new Set(
		nonEmptyColumns.map((column) => column.id).filter((id) => id > 0),
	)

	const inflatedTechnicalColumns = (table.stereotypeColumns || [])
		.map((col) => inflateColumns(col))
		.map(
			(col) =>
				({
					...col,
					id: stableColumnId(col.id, usedColumnIds),
					stereotypeColumn: true,
				}) as TableColumn,
		)

	const columns = assignCollectionOrder([
		...nonEmptyColumns,
		...inflatedTechnicalColumns,
	])

	columns.forEach((column) => {
		if (column.stereotype?.code !== 'VIRTUAL_COLUMN') {
			delete column.virtualColumnScript
		}
	})

	const formData = {
		...table.form,
		columns,
		indexes: assignCollectionOrder(table.form.indexes.filter((x) => x.name)),
		constraints: assignCollectionOrder(
			table.form.constraints.filter((x) => x.name),
		),
		objectPermissions: assignCollectionOrder(
			table.form.objectPermissions || [],
		),
	}

	// Check reference table data
	if (formData.hasReferenceTable) {
		if (!formData.referenceTable) {
			formData.referenceTable = defaultReferenceTableData()
		}

		if (
			!formData.referenceTable.code?.length &&
			!formData.referenceTable.objectSettings?.length
		) {
			// load default object setting on first init
			const osDefault = await dispatch(
				getDefaultObjectSettings(NewStructureDto.TypeEnum.TABLE),
			)

			// Create a new object instead of mutating the existing one
			formData.referenceTable = {
				...formData.referenceTable,
				objectSettings: osDefault ? [osDefault] : [],
			}
		}

		if (shouldGenerateCode) {
			const sharedConfig = [
				Number(node.id),
				SqlTypeEnum.TABLE_REFERENCE,
				{
					master_node_name: formData.name,
					master_node_code: formData.code,
					body: { data: JSON.stringify(formData.referenceTable) },
				},
			] as const

			const namings = [
				dispatch(
					callNamingWorkingData([
						NamingDtoTypeEnum.REFERENCE_TABLE_NAME,
						...sharedConfig,
					]),
				),
				dispatch(
					callNamingWorkingData([
						NamingDtoTypeEnum.REFERENCE_TABLE_CODE,
						...sharedConfig,
					]),
				),
			]

			try {
				const namingsResults = await Promise.all(namings)

				formData.referenceTable = {
					...formData.referenceTable,
					name: namingsResults[0]?.result,
					code: formatCodeByCase(
						namingsResults[1]?.result,
						isUpperCase as boolean,
					),
				}
			} catch (e) {
				logError(e, { operation: 'getTableData.generateReferenceNaming' })
			}
		}
	}

	// Check history table data
	if (formData.hasHistoryTable) {
		if (!formData.historyTable) {
			formData.historyTable = defaultHistoryTableData()
		}

		if (
			!formData.historyTable.code?.length &&
			!formData.historyTable.objectSettings?.length
		) {
			// load default object setting on first init
			const osDefault = await dispatch(
				getDefaultObjectSettings(NewStructureDto.TypeEnum.TABLE),
			)

			// Clone formData.historyTable and then change objectSettings
			formData.historyTable = {
				...formData.historyTable,
				name: hasHistoryTable ? `${formData.name} History` : undefined,
				objectSettings: osDefault ? [osDefault] : [],
			}
		}

		if (shouldGenerateCode) {
			const sharedConfig = [
				Number(node.id),
				SqlTypeEnum.TABLE_HISTORY,
				{
					master_node_name: formData.name,
					master_node_code: formData.code,
					body: { data: JSON.stringify(formData.historyTable) },
				},
			] as const

			const namings = [
				dispatch(
					callNamingWorkingData([
						NamingDtoTypeEnum.HISTORICAL_TABLE_NAME,
						...sharedConfig,
					]),
				),
				dispatch(
					callNamingWorkingData([
						NamingDtoTypeEnum.HISTORICAL_TABLE_CODE,
						...sharedConfig,
					]),
				),
			]

			try {
				const namingsResults = await Promise.all(namings)

				formData.historyTable = {
					...formData.historyTable,
					name: namingsResults[0]?.result,
					code: formatCodeByCase(
						namingsResults[1]?.result,
						isUpperCase as boolean,
					),
				}
			} catch (e) {
				logError(e, { operation: 'getTableData.generateHistoryNaming' })
			}
		}

		// InflateColumns in historyTable
		const nonEmptyColumns =
			formData.historyTable?.columns
				?.filter((x) => x.name)
				.map((col) => inflateColumns(col)) ?? []

		formData.historyTable = {
			...formData.historyTable,
			columns: assignCollectionOrder(nonEmptyColumns),
		}
	}

	return formData as Table
}
